import React, { useRef, useState } from "react";
import Slider from "react-slick";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Button,
  Box,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

const isVideo = (url) => {
  if (typeof url !== "string") return false;
  return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
};

const RentalYieldCard = ({
  id,
  name,
  address,
  totalamount,
  amenities = [],
  nearby = [],
  persqft,
  images = [],
  verify,
  area,
  soldStatus,
  sold_status,
}) => {
  const isSold = soldStatus === 1 || sold_status === 1;
  const previewSliderRef = useRef(null);
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});
  const [hoveredVideo, setHoveredVideo] = useState(null);

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = videoRefs.current[src];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const validImages = Array.isArray(images) && images.length > 0
    ? images
    : ["/assets/images/no-image.png"];

  const showArrows = validImages.length > 1;


  const safeAmenity = Array.isArray(amenities) ? amenities : [];
  const safeNearBy = Array.isArray(nearby) ? nearby : [];
  const limitToThree = (arr) => Array.isArray(arr) ? arr.slice(0, 3) : [];
  const amenityList = limitToThree(safeAmenity);
  const nearByList = limitToThree(safeNearBy);

  const navi = useNavigate();

  const handleOpenPreview = (index) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "rgba(0,0,0,0.85)",
          },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: "relative", height: { xs: 300, sm: 480, md: 600 }, overflow: "hidden" }}>
            {showArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider ref={previewSliderRef} initialSlide={activeIndex}
              beforeChange={(oldIndex, newIndex) => {
                const vid = videoRefs.current[validImages[oldIndex]];
                if (vid) vid.pause();
                setPlayingVideo(null);
                setActiveIndex(newIndex);
              }}
              dots infinite={false} speed={400} slidesToShow={1} slidesToScroll={1} arrows={false}>
              {validImages.map((img, i) => (
                <Box key={i}>
                  <Box sx={{
                    height: { xs: 300, sm: 480, md: 600 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.7)",
                    p: 1,
                    position: "relative"
                  }}>
                    {!loadedMedia[img] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                    )}
                    {isVideo(img) ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <video
                          ref={(el) => (videoRefs.current[img] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(img)}
                          onClick={(e) => togglePlay(e, img)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: loadedMedia[img] ? "block" : "none",
                            cursor: "pointer",
                          }}
                        />
                        {/* Standard Rectangular Play/Pause Buttons */}
                        {loadedMedia[img] && (
                          <Box
                            onClick={(e) => togglePlay(e, img)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              width: 60,
                              height: 50,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "4px",
                              zIndex: 2,
                            }}
                          >
                            {playingVideo === img ? (
                              <PauseIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 40 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <img
                        src={img}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {showArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Card
        onClick={() => navi(`/rental-yield-plots/details/${id}`)}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: 4,
          p: { xs: 1, sm: 1.5 },
          overflow: "hidden",
          width: "100%",
          mx: "auto",
          gap: { xs: 1, sm: 2 },
          backgroundColor: "#FBFBF8",
          boxShadow: "0 4px 12px 6px rgba(0,0,0,0.08)",
          height: "auto",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
          },
        }}
      >
        {/* IMAGE SECTION */}
        <Box
          sx={{ position: "relative", width: { xs: "100%", sm: "42%" }, flexShrink: 0, pt: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 13,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              px: 1,
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            {isVideo(validImages[0]) ? (
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  overflow: "hidden",
                  borderRadius: 3,
                  position: "relative"
                }}
              >
                {!loadedMedia[validImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0 }}
                  />
                )}
                <Box
                  onMouseEnter={() => setHoveredVideo(validImages[0])}
                  onMouseLeave={() => setHoveredVideo(null)}
                  sx={{ position: "relative", width: "100%", height: "100%" }}
                >
                  <video
                    ref={(el) => (videoRefs.current[validImages[0]] = el)}
                    src={validImages[0]}
                    playsInline
                    disablePictureInPicture
                    disableRemotePlayback
                    controlsList="nodownload nopictureinpicture"
                    preload="metadata"
                    onLoadedData={() => handleMediaLoad(validImages[0])}
                    onClick={(e) => {
                      if (playingVideo !== validImages[0]) {
                        togglePlay(e, validImages[0]);
                      } else {
                        handleOpenPreview(0);
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: loadedMedia[validImages[0]] ? "block" : "none",
                      cursor: "pointer",
                    }}
                  />
                  {loadedMedia[validImages[0]] && (
                    <Box
                      onClick={(e) => togglePlay(e, validImages[0])}
                      onMouseEnter={() => setHoveredVideo(validImages[0])}
                      onMouseLeave={() => setHoveredVideo(null)}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        width: 50,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "4px",
                        opacity: playingVideo === validImages[0] ? (hoveredVideo === validImages[0] ? 1 : 0) : 1,
                        transition: "opacity 0.2s ease",
                        zIndex: 2,
                      }}
                    >
                      {playingVideo === validImages[0] ? (
                        <PauseIcon sx={{ fontSize: 30 }} />
                      ) : (
                        <PlayArrowIcon sx={{ fontSize: 30 }} />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  overflow: "hidden",
                  borderRadius: 3,
                  position: "relative"
                }}
              >
                {!loadedMedia[validImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0 }}
                  />
                )}
                <CardMedia
                  component="img"
                  image={validImages[0]}
                  onLoad={() => handleMediaLoad(validImages[0])}
                  onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    cursor: "zoom-in",
                    transition: "transform 0.3s ease",
                    display: loadedMedia[validImages[0]] ? "block" : "none",
                    "&:hover": { transform: "scale(1.04)" },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: { xs: 1, sm: 1.5 },
            opacity: isSold ? 0.65 : 1,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography sx={{ fontWeight: { xs: 500, sm: 600, lg: 700 }, fontSize: { xs: "0.95rem", sm: "1.05rem", lg: "1.2rem" }, color: "#9DC354" }}>
              {name || ""}
            </Typography>

            {/* LOCATION */}
            <Box display="flex" alignItems="center" mt={0.5} mb={0.8}>
              <LocationOn sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem", lg: "1.4rem" }, color: "#9DC354", mr: 0.5 }} />
              <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#707070", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {address || ""}
              </Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#FEB821", my: 0.8, borderWidth: 1.5 }} />

            {/* PRICE ROW */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.8, gap: 1.5, flexWrap: "wrap", width: "100%" }}>
              <PriceBlock
                icon={<CurrencyRupeeIcon sx={{ color: "#9DC354", fontSize: 18 }} />}
                value={totalamount}
                label="Total Amount"
              />
              <VDivider />
              <PriceBlock
                icon={<CurrencyRupeeIcon sx={{ color: "#9DC354", fontSize: 18 }} />}
                value={persqft}
                label="Per sqft"
              />
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#FEB821", my: 0.8, borderWidth: 1.5 }} />

            {/* Nearby */}
            <InfoRow title="Near By" data={nearByList} />

            {/* Amenities */}
            <InfoRow title="Amenities" data={amenityList} />
          </CardContent>

          {/* BUTTON */}
          <Box display="flex" justifyContent="center" mt={1.5}>
            <Button
              variant="contained"
              sx={{
                width: 140,
                height: 36,
                textTransform: "none",
                bgcolor: "#FDBA15",
                color: "black",
                "&:hover": { bgcolor: "#e3a30f" },
                borderRadius: 20,
                fontWeight: 500,
                fontSize: 14,
                opacity: isSold ? 0.65 : 1,
              }}
              onClick={(e) => { e.stopPropagation(); navi(`/rental-yield-plots/details/${id}`); }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default RentalYieldCard;

/*──── Sub-components ────*/

const PriceBlock = ({ icon, value, unit, label }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.4}>
      {icon}
      <Typography sx={{ fontSize: { xs: 14, sm: 14 }, fontWeight: 600 }}>{value || "-"}</Typography>
      <Typography sx={{ fontSize: { xs: 14, sm: 14 }, fontWeight: 600 }}>{unit}</Typography>
    </Box>
    <Typography color="text.secondary" sx={{ fontSize: { xs: 14, sm: 14 } }}>{label}</Typography>
  </Box>
);

const InfoRow = ({ title, data }) => (
  <Box mt={1}>
    <Typography fontWeight={500} sx={{ fontSize: { xs: 12, sm: 13, md: 14 } }}>
      {title}:{" "}
      <Typography component="span" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 13, md: 14 } }}>
        {data?.length
          ? data.map((item, index) => (
            <span key={index}>
              {item.title}
              {index !== data.length - 1 && " • "}
            </span>
          ))
          : "No Data Found"}
      </Typography>
    </Typography>
  </Box>
);

const VDivider = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{ borderStyle: "dashed", borderColor: "#FEB821", mx: 1.2, borderWidth: 1.3 }}
  />
);
